import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { AppraisalsSection, MyDetailsSection, MyPastEvaluationSection, ToDoSection } from '@/components/dashboard';
import { paths } from '@/routes/paths';
import { useAuthStore } from '@/stores/useAuthStore';
import { useActiveStaff, useEmployeeDashboard } from '@/queries/useOCCerSearch';
import type { StaffSearchResult } from '@/api/occerSearchApi';

export function OCCerSearchPage() {
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Staffing', 'HR']);

  const [selectedEmployee, setSelectedEmployee] = useState<StaffSearchResult | null>(null);

  // All hooks must be called before any conditional returns
  const { data: staffData, isLoading: isLoadingStaff } = useActiveStaff();
  const { data: dashboardData, isLoading: isLoadingDashboard, isError: isDashboardError } = useEmployeeDashboard(
    selectedEmployee?.employeeId ?? null
  );

  // Redirect if user doesn't have required roles
  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  const employeeName = selectedEmployee
    ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
    : null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {employeeName ? `OC&CER SEARCH - ${employeeName}` : 'OC&CER SEARCH'}
        </Typography>

        <Autocomplete
          options={staffData || []}
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName} (${option.billingGrade})`
          }
          value={selectedEmployee}
          onChange={(_, newValue) => {
            setSelectedEmployee(newValue);
          }}
          isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
          loading={isLoadingStaff}
          noOptionsText="No employees found"
          slotProps={{ listbox: { style: { maxHeight: 400 } } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employee"
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingStaff ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          sx={{ width: 280 }}
        />
      </Box>

      {isLoadingDashboard && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {isDashboardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load dashboard. Please try again.
        </Alert>
      )}

      {dashboardData && (
        <>
          {dashboardData.details && <MyDetailsSection myDetails={dashboardData.details} />}
          <ToDoSection
            jobEnds={dashboardData.jobEnds ?? []}
            seniorFeedback={dashboardData.seniorFeedback ?? []}
            qualtricsForm={(dashboardData.qualtricsForm ?? []).map(qf => ({ ...qf, link: '' }))}
            suppressQualtricsLinks
          />
          <AppraisalsSection
            myAppraisal={dashboardData.myAppraisal ?? null}
            collation={dashboardData.collation ?? []}
            plannedInterviews={dashboardData.plannedInterviews ?? []}
            talentCommittees={dashboardData.talentCommittees ?? []}
          />
          <MyPastEvaluationSection
            pastEvaluations={dashboardData.pastEvaluations ?? []}
            appraisals={dashboardData.appraisals ?? []}
          />
        </>
      )}
    </Box>
  );
}

export default OCCerSearchPage;
